import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, Link } from "react-router-dom";
import { AppSidebar } from "@/components/app-sidebar";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { envelopeApi, templateApi, clauseApi } from "@/services/api";

// Check if a string is a UUID
const isUUID = (str) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

// Capitalize first letter of a string
const capitalize = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Format path segment to readable label
const formatPathSegment = (segment) =>
  // Replace hyphens and underscores with spaces, then capitalize each word
  segment
    .replace(/[-_]/g, " ")
    .split(" ")
    .map((word) => capitalize(word))
    .join(" ");
export const DashboardLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [dynamicLabels, setDynamicLabels] = useState({});

  // Static path labels mapping
  const pathLabels = {
    dashboard: t("dashboard.title"),
    envelopes: t("envelopes.title"),
    templates: t("templates.title"),
    folders: t("folders.title"),
    tags: t("tags.title"),
    "merge-keys": t("mergeKeys.title"),
    "api-keys": t("apiKeys.title"),
    team: t("team.title"),
    usage: t("usage.title"),
    billing: t("billing.title"),
    settings: t("common.settings"),
    reminders: t("reminders.title"),
    "document-tools": t("documentTools.title"),
    clauses: t("clauses.title"),
    "clause-library": t("clauses.title"),
    "signing-links": t("signingLinks.title"),
    "bulk-send": t("bulkSend.title"),
    analytics: t("analytics.title"),
    embed: t("embed.title"),
    "in-person": t("inPerson.title"),
    activity: t("activity.title"),
    new: t("common.new"),
    edit: t("common.edit"),
    admin: t("admin.title"),
    users: t("admin.users.title"),
    branding: t("branding.title"),
    organisation: t("organisation.title"),
    webhooks: t("webhooks.title"),
    security: t("security.title"),
    compliance: t("compliance.title"),
  };

  // Fetch dynamic labels for UUIDs
  useEffect(() => {
    const paths = location.pathname.split("/").filter(Boolean);

    paths.forEach((path, index) => {
      if (isUUID(path) && !dynamicLabels[path]) {
        const parentPath = paths[index - 1];

        // Fetch resource name based on parent path
        const fetchResourceName = async () => {
          try {
            let name = null;

            if (parentPath === "envelopes") {
              const response = await envelopeApi.getById(path);
              name = response.data?.data?.title || response.data?.title;
            } else if (parentPath === "templates") {
              const response = await templateApi.getById(path);
              name = response.data?.data?.name || response.data?.name;
            } else if (parentPath === "clauses") {
              const response = await clauseApi.getById(path);
              name = response.data?.data?.name || response.data?.name;
            }

            if (name) {
              setDynamicLabels((prev) => ({ ...prev, [path]: name }));
            }
          } catch (error) {
            console.error("Failed to fetch resource name:", error);
          }
        };

        fetchResourceName();
      }
    });
  }, [dynamicLabels, location?.pathname]);

  // Generate breadcrumb items from current path
  const getBreadcrumbs = () => {
    const paths = location.pathname.split("/").filter(Boolean);
    const breadcrumbs = [];

    paths.forEach((path, index) => {
      const href = `/${paths.slice(0, index + 1).join("/")}`;
      const isLast = index === paths.length - 1;

      let label;
      if (isUUID(path)) {
        // Use dynamic label if available, otherwise show loading indicator
        label = dynamicLabels[path] || "...";
      } else if (pathLabels[path]) {
        label = pathLabels[path];
      } else {
        // Format unknown paths nicely
        label = formatPathSegment(path);
      }

      breadcrumbs.push({
        label: capitalize(label),
        href,
        isLast,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        {/* Header with breadcrumb */}
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between gap-2 border-b bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb) => (
                  <React.Fragment key={crumb.href}>
                    <BreadcrumbItem>
                      {crumb.isLast ? (
                        <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={crumb.href}>{crumb.label}</Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {!crumb.isLast && <BreadcrumbSeparator />}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="flex items-center gap-2 px-4">
            <ThemeToggle />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="p-4 md:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default DashboardLayout;
